import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import App from './App'
import { api } from './lib/api'
import { loadCatalogue } from './lib/i18n'
import { preferredLocale } from './lib/i18n/I18nProvider'
import { localeFromPath } from './lib/i18n/paths'
import { expectStaticLanding } from './lib/prerender'
import { startTheme } from './lib/theme'
// The UI's typefaces, served with the app rather than from Google Fonts: no
// third-party request on any page, and they work offline. Each file is split
// by script (unicode-range), so a page downloads only the alphabets it shows.
import '@fontsource-variable/inter/wght.css'
import '@fontsource/spectral/400.css'
import '@fontsource/spectral/500.css'
import '@fontsource/spectral/600.css'
import '@fontsource/spectral/400-italic.css'
import '@fontsource/spectral/500-italic.css'
import '@fontsource/ibm-plex-mono/400.css'
import '@fontsource/ibm-plex-mono/500.css'
import './styles/tokens.css'
import './styles/base.css'
import './styles/editor.css'
import './styles/canvas.css'
import './styles/landing.css'

startTheme()

const container = document.getElementById('root')
if (!container) throw new Error('Missing #root element')
const root = createRoot(container)

// The built index.html carries the landing page as static HTML (lib/prerender.ts).
// It stays on screen while the app boots, and React's first commit swaps it for
// the same page. The inline script in index.html has already hidden it where it
// would be wrong — another route, another language — and then there is a
// spinner to look at instead.
const staticLanding =
  container.dataset.prerendered === 'landing' && document.documentElement.dataset.static !== 'none'
if (!staticLanding) {
  // The style that hid the static page keys off this attribute; React clears
  // the children, not the container's attributes.
  delete container.dataset.prerendered
  root.render(
    <div className="boot">
      <div className="boot-mark" />
    </div>,
  )
}

// Storage opens alongside; only the plan routes wait for it (App.tsx). The
// landing page must not: an in-app browser that refuses IndexedDB still gets
// the page a shared link promised.
const storage = api.ready()
storage.catch(() => {
  /* App reads the outcome; this only keeps an early failure from being reported as unhandled */
})

/**
 * Bootstrap: the language before the first render of the app, so a visitor
 * never sees a frame of a language that is not theirs. A language page's URL
 * names its own (`/ru`); everywhere else the stored preference or the browser
 * decides. There is no account and no server; planning starts on the first click.
 */
async function boot(): Promise<void> {
  const locale = localeFromPath(window.location.pathname) ?? preferredLocale()
  const catalogue = await loadCatalogue(locale)
  if (staticLanding) expectStaticLanding()
  root.render(
    <StrictMode>
      <App locale={locale} catalogue={catalogue} storage={storage} />
    </StrictMode>,
  )
}

void boot()

// Offline support (src/pwa/sw.ts). Production only: in development the worker
// would serve yesterday's modules to a hot-reloading page.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // Without it the app still works; it just needs the network to open.
    })
  })
}
